/**
 * CR164: Deal Locator Section for S0 Scan Settings
 * Configures Deep Keyword Scan, tiled scanning, and extraction focus
 */

import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Search, Grid3X3, Crosshair, RotateCcw, 
  AlertCircle, CheckCircle, Info 
} from 'lucide-react';
import {
  type DealLocatorConfig,
  type TiledScanningConfig,
  type ExtractionFocusConfig,
  DEFAULT_DEAL_LOCATOR_CONFIG,
  DEFAULT_TILED_SCANNING_CONFIG,
  DEFAULT_EXTRACTION_FOCUS_CONFIG,
  DEFAULT_DEAL_LOCATOR_KEYWORDS,
  parseKeywordsFromText,
} from '@/lib/flyer-pipeline-v2/dealLocatorTypes';

interface S0DealLocatorSectionProps {
  locatorConfig: DealLocatorConfig;
  tiledConfig: TiledScanningConfig;
  extractionConfig: ExtractionFocusConfig;
  onLocatorConfigChange: (config: DealLocatorConfig) => void;
  onTiledConfigChange: (config: TiledScanningConfig) => void;
  onExtractionConfigChange: (config: ExtractionFocusConfig) => void;
  disabled?: boolean;
}

export function S0DealLocatorSection({
  locatorConfig,
  tiledConfig,
  extractionConfig,
  onLocatorConfigChange,
  onTiledConfigChange,
  onExtractionConfigChange,
  disabled = false,
}: S0DealLocatorSectionProps) {
  const [keywordsText, setKeywordsText] = useState(
    locatorConfig.keywords.join('\n')
  );

  const handleKeywordsChange = (text: string) => {
    setKeywordsText(text);
    const keywords = parseKeywordsFromText(text);
    onLocatorConfigChange({ ...locatorConfig, keywords });
  };

  const handleResetKeywords = () => {
    const defaultText = DEFAULT_DEAL_LOCATOR_KEYWORDS.join('\n');
    setKeywordsText(defaultText);
    onLocatorConfigChange({ 
      ...locatorConfig, 
      keywords: DEFAULT_DEAL_LOCATOR_KEYWORDS 
    });
  };

  const activeKeywordsCount = locatorConfig.keywords.filter(k => k.length > 0).length;

  return (
    <Accordion type="multiple" className="space-y-2">
      {/* Section 2: Deal Locator — Deep Keyword Scan */}
      <AccordionItem value="locator" className="border rounded-lg">
        <AccordionTrigger className="px-4 py-3 hover:no-underline">
          <div className="flex items-center gap-2 text-sm">
            <Search className="h-4 w-4" />
            <span className="font-medium">Deal Locator — Deep Keyword Scan</span>
            <Badge 
              variant={locatorConfig.enabled ? "default" : "secondary"}
              className="text-xs"
            >
              {locatorConfig.enabled ? "ON" : "OFF"}
            </Badge>
            {locatorConfig.enabled && (
              <span className="text-xs text-muted-foreground">
                {activeKeywordsCount} keywords
              </span>
            )}
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-4">
          {/* Master Toggle */}
          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="text-xs font-medium">Enable Deep Keyword Scan</Label>
              <p className="text-xs text-muted-foreground">
                Locate deal regions on pages after signal detection
              </p>
            </div>
            <Switch
              checked={locatorConfig.enabled}
              onCheckedChange={(v) => onLocatorConfigChange({ ...locatorConfig, enabled: v })}
              disabled={disabled}
            />
          </div>

          {locatorConfig.enabled && (
            <>
              {/* Run Mode */}
              <div className="space-y-2 py-2 border-t">
                <Label className="text-xs font-medium">Run Mode</Label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="runMode"
                      checked={locatorConfig.runOnCandidatesOnly}
                      onChange={() => onLocatorConfigChange({ 
                        ...locatorConfig, 
                        runOnCandidatesOnly: true, 
                        runOnAllPages: false 
                      })}
                      disabled={disabled}
                      className="w-4 h-4"
                    />
                    <span className="text-xs">Candidate pages only</span>
                    <Badge variant="outline" className="text-xs">Default</Badge>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="runMode"
                      checked={locatorConfig.runOnAllPages}
                      onChange={() => onLocatorConfigChange({ 
                        ...locatorConfig, 
                        runOnCandidatesOnly: false, 
                        runOnAllPages: true 
                      })}
                      disabled={disabled}
                      className="w-4 h-4"
                    />
                    <span className="text-xs">All pages</span>
                  </label>
                </div>
              </div>

              {/* Output Settings */}
              <div className="grid grid-cols-2 gap-4 py-2 border-t">
                <div className="space-y-1">
                  <Label className="text-xs">Create Locator Regions</Label>
                  <Switch
                    checked={locatorConfig.createLocatorRegions}
                    onCheckedChange={(v) => onLocatorConfigChange({ 
                      ...locatorConfig, 
                      createLocatorRegions: v 
                    })}
                    disabled={disabled}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Max Regions Per Page</Label>
                  <Input
                    type="number"
                    min={1}
                    max={20}
                    value={locatorConfig.maxRegionsPerPage}
                    onChange={(e) => onLocatorConfigChange({ 
                      ...locatorConfig, 
                      maxRegionsPerPage: parseInt(e.target.value) || 5 
                    })}
                    disabled={disabled}
                    className="h-8 text-xs w-20"
                  />
                </div>
              </div>

              {/* Keywords */}
              <div className="space-y-2 py-2 border-t">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium">Keywords (one per line)</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleResetKeywords}
                    disabled={disabled}
                    className="h-6 text-xs"
                  >
                    <RotateCcw className="h-3 w-3 mr-1" />
                    Reset Defaults
                  </Button>
                </div>
                <Textarea
                  value={keywordsText}
                  onChange={(e) => handleKeywordsChange(e.target.value)}
                  disabled={disabled}
                  placeholder="gift card&#10;bonus&#10;when you spend"
                  className="text-xs font-mono min-h-[120px]"
                />
                <p className="text-xs text-muted-foreground">
                  Normalization: case-insensitive, trim, collapse spaces
                </p>
              </div>

              {/* Tight Matching Controls */}
              <div className="space-y-3 py-2 border-t">
                <Label className="text-xs font-medium flex items-center gap-1">
                  Tight Matching Controls
                  <Info className="h-3 w-3 text-muted-foreground" />
                </Label>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-xs">Require gift-card context</Label>
                      <p className="text-xs text-muted-foreground">
                        Non-GC keywords (bonus, points) must have gift indicator nearby
                      </p>
                    </div>
                    <Switch
                      checked={locatorConfig.tightMatching.requireGiftCardContext}
                      onCheckedChange={(v) => onLocatorConfigChange({
                        ...locatorConfig,
                        tightMatching: { ...locatorConfig.tightMatching, requireGiftCardContext: v }
                      })}
                      disabled={disabled}
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <Label className="text-xs whitespace-nowrap">Context window (chars)</Label>
                    <Input
                      type="number"
                      min={20}
                      max={200}
                      value={locatorConfig.tightMatching.contextWindowChars}
                      onChange={(e) => onLocatorConfigChange({
                        ...locatorConfig,
                        tightMatching: { 
                          ...locatorConfig.tightMatching, 
                          contextWindowChars: parseInt(e.target.value) || 80 
                        }
                      })}
                      disabled={disabled || !locatorConfig.tightMatching.requireGiftCardContext}
                      className="h-8 text-xs w-20"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-xs">Require money cue for money phrases</Label>
                      <p className="text-xs text-muted-foreground">
                        "get a $", "spend $" must have currency pattern
                      </p>
                    </div>
                    <Switch
                      checked={locatorConfig.tightMatching.requireMoneyCue}
                      onCheckedChange={(v) => onLocatorConfigChange({
                        ...locatorConfig,
                        tightMatching: { ...locatorConfig.tightMatching, requireMoneyCue: v }
                      })}
                      disabled={disabled}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </AccordionContent>
      </AccordionItem>

      {/* Section 3: Large Page Tiled Scanning */}
      <AccordionItem value="tiled" className="border rounded-lg">
        <AccordionTrigger className="px-4 py-3 hover:no-underline">
          <div className="flex items-center gap-2 text-sm">
            <Grid3X3 className="h-4 w-4" />
            <span className="font-medium">Large Page Tiled Scanning</span>
            <Badge 
              variant={tiledConfig.enabled ? "default" : "secondary"}
              className="text-xs"
            >
              {tiledConfig.enabled ? "ON" : "OFF"}
            </Badge>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-4">
          {/* Master Toggle */}
          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="text-xs font-medium">Enable Tiled Scanning</Label>
              <p className="text-xs text-muted-foreground">
                Split large pages into tiles for better OCR accuracy
              </p>
            </div>
            <Switch
              checked={tiledConfig.enabled}
              onCheckedChange={(v) => onTiledConfigChange({ ...tiledConfig, enabled: v })}
              disabled={disabled}
            />
          </div>

          {tiledConfig.enabled && (
            <>
              {/* Large Page Thresholds */}
              <div className="space-y-3 py-2 border-t">
                <Label className="text-xs font-medium">Large Page Threshold</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      Longest edge (px)
                    </Label>
                    <Input
                      type="number"
                      min={2000}
                      max={10000}
                      step={500}
                      value={tiledConfig.largePageEdgeThreshold}
                      onChange={(e) => onTiledConfigChange({
                        ...tiledConfig,
                        largePageEdgeThreshold: parseInt(e.target.value) || 5000
                      })}
                      disabled={disabled}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      Area (px²)
                    </Label>
                    <Input
                      type="number"
                      min={10_000_000}
                      max={100_000_000}
                      step={5_000_000}
                      value={tiledConfig.largePageAreaThreshold}
                      onChange={(e) => onTiledConfigChange({
                        ...tiledConfig,
                        largePageAreaThreshold: parseInt(e.target.value) || 25_000_000
                      })}
                      disabled={disabled}
                      className="h-8 text-xs"
                    />
                    <span className="text-xs text-muted-foreground">
                      ~{(tiledConfig.largePageAreaThreshold / 1_000_000).toFixed(0)} MP
                    </span>
                  </div>
                </div>
              </div>

              {/* Tile Settings */}
              <div className="grid grid-cols-2 gap-3 py-2 border-t">
                <div className="space-y-1">
                  <Label className="text-xs">Tile Size (px)</Label>
                  <Input
                    type="number"
                    min={512}
                    max={4096}
                    step={128}
                    value={tiledConfig.tileSizePx}
                    onChange={(e) => onTiledConfigChange({
                      ...tiledConfig,
                      tileSizePx: parseInt(e.target.value) || 1536
                    })}
                    disabled={disabled}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tile Overlap (px)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={512}
                    step={32}
                    value={tiledConfig.tileOverlapPx}
                    onChange={(e) => onTiledConfigChange({
                      ...tiledConfig,
                      tileOverlapPx: parseInt(e.target.value) || 192
                    })}
                    disabled={disabled}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            </>
          )}
        </AccordionContent>
      </AccordionItem>

      {/* Section 4: Extraction Focus */}
      <AccordionItem value="extraction" className="border rounded-lg">
        <AccordionTrigger className="px-4 py-3 hover:no-underline">
          <div className="flex items-center gap-2 text-sm">
            <Crosshair className="h-4 w-4" />
            <span className="font-medium">Extraction Focus</span>
            <Badge 
              variant={extractionConfig.preferLocatorCrops ? "default" : "secondary"}
              className="text-xs"
            >
              {extractionConfig.preferLocatorCrops ? "Crops" : "Full Page"}
            </Badge>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4 space-y-4">
          {/* Master Toggle */}
          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="text-xs font-medium">Prefer Locator Crops for AI</Label>
              <p className="text-xs text-muted-foreground">
                Send cropped deal regions to AI instead of full page
              </p>
            </div>
            <Switch
              checked={extractionConfig.preferLocatorCrops}
              onCheckedChange={(v) => onExtractionConfigChange({ 
                ...extractionConfig, 
                preferLocatorCrops: v 
              })}
              disabled={disabled}
            />
          </div>

          {extractionConfig.preferLocatorCrops && (
            <>
              {/* Crop Settings */}
              <div className="grid grid-cols-2 gap-3 py-2 border-t">
                <div className="space-y-1">
                  <Label className="text-xs">Crop Padding (px)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={200}
                    step={10}
                    value={extractionConfig.cropPaddingPx}
                    onChange={(e) => onExtractionConfigChange({
                      ...extractionConfig,
                      cropPaddingPx: parseInt(e.target.value) || 80
                    })}
                    disabled={disabled}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Max Crops Per Page</Label>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={extractionConfig.maxCropsPerPage}
                    onChange={(e) => onExtractionConfigChange({
                      ...extractionConfig,
                      maxCropsPerPage: parseInt(e.target.value) || 3
                    })}
                    disabled={disabled}
                    className="h-8 text-xs"
                  />
                </div>
              </div>

              {/* Fallback Behavior */}
              <div className="flex items-center justify-between py-2 border-t">
                <div>
                  <Label className="text-xs">Fallback to Full Page</Label>
                  <p className="text-xs text-muted-foreground">
                    If no locator regions found, run full-page AI extraction
                  </p>
                </div>
                <Switch
                  checked={extractionConfig.fallbackToFullPage}
                  onCheckedChange={(v) => onExtractionConfigChange({
                    ...extractionConfig,
                    fallbackToFullPage: v
                  })}
                  disabled={disabled}
                />
              </div>
            </>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
